/**
 * Horizontal product carousel built on native scroll-snap.
 * Arrows only appear when the track overflows; touch/trackpad scrolling works without JS.
 */
class LeatherCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.lp-carousel__track');
    this.buttons = [...this.querySelectorAll('.lp-carousel__arrow')];
    if (!this.track) return;

    this.update = this.update.bind(this);
    this.addEventListener('click', (event) => {
      const button = event.target.closest('.lp-carousel__arrow');
      if (!button) return;
      const slide = this.track.querySelector('.lp-carousel__slide');
      const step = slide ? slide.getBoundingClientRect().width : this.track.clientWidth;
      const perPage = Math.max(1, Math.floor(this.track.clientWidth / step));
      this.track.scrollBy({ left: Number(button.dataset.dir) * step * perPage, behavior: 'smooth' });
    });

    this.track.addEventListener('scroll', this.update, { passive: true });
    this.resizeObserver = new ResizeObserver(this.update);
    this.resizeObserver.observe(this.track);
    this.update();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const overflows = scrollWidth > clientWidth + 2;
    const [prev, next] = this.buttons;
    this.buttons.forEach((button) => (button.hidden = !overflows));
    if (prev) prev.disabled = scrollLeft <= 2;
    if (next) next.disabled = scrollLeft + clientWidth >= scrollWidth - 2;
  }
}

if (!customElements.get('leather-carousel')) customElements.define('leather-carousel', LeatherCarousel);

/**
 * Promo announcement slider: arrows, dots and optional autoplay that pauses on hover/focus
 * and respects reduced-motion preferences.
 */
class LeatherAnnouncement extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.lp-ann__slide')];
    this.dots = [...this.querySelectorAll('.lp-ann__dot')];
    this.index = 0;
    if (this.slides.length < 2) return;

    this.addEventListener('click', (event) => {
      const arrow = event.target.closest('[data-step]');
      if (!arrow) return;
      this.show(this.index + Number(arrow.dataset.step));
      this.restart();
    });

    const seconds = Number(this.dataset.autoplay) || 0;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.delay = reducedMotion ? 0 : seconds * 1000;
    ['mouseenter', 'focusin'].forEach((type) => this.addEventListener(type, () => this.stop()));
    ['mouseleave', 'focusout'].forEach((type) => this.addEventListener(type, () => this.restart()));
    this.restart();
  }

  disconnectedCallback() {
    this.stop();
  }

  show(next) {
    this.index = (next + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => (slide.hidden = i !== this.index));
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
  }

  stop() {
    clearInterval(this.timer);
  }

  restart() {
    this.stop();
    if (this.delay) this.timer = setInterval(() => this.show(this.index + 1), this.delay);
  }
}

if (!customElements.get('leather-announcement')) customElements.define('leather-announcement', LeatherAnnouncement);

/**
 * Custom product-page lines (price with comp value) follow the selected variant.
 * Horizon broadcasts the re-rendered section with every variant change; swap in the matching markup.
 */
document.addEventListener('shopify:product:select', (event) => {
  if (event.target instanceof Element && event.target.closest('product-card')) return;
  event.promise
    ?.then(({ detail }) => {
      const html = detail?.html;
      if (!html) return;
      document.querySelectorAll('[data-lp-sync]').forEach((element) => {
        const fresh = html.querySelector(`[data-lp-sync="${element.dataset.lpSync}"]`);
        if (fresh) element.replaceWith(fresh.cloneNode(true));
      });
    })
    .catch(() => {});
});
